import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/shadcn/custom/DateTimePicker/date-time-picker';
import { Textarea } from '@/components/ui/textarea';
import type { CreateOrgActionReqData } from 'components/Actions/hooks/orgActionHooks';
import type {
  CustomEventAction,
  CustomEventActionSeverity,
} from 'hooks/customEvents/useCustomEvents';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

const UNASSIGNED = 'unassigned';
const NO_SEVERITY = 'none';
const SEVERITY_ITEMS: {
  value: CustomEventActionSeverity | typeof NO_SEVERITY;
  label: string;
}[] = [
  { value: NO_SEVERITY, label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export type EventActionInput = Pick<
  CreateOrgActionReqData,
  'name' | 'description' | 'poc' | 'startDate' | 'targetDate'
> & { severity: CustomEventActionSeverity | null };

type EventActionFormValues = {
  name: string;
  description: string;
  poc: string;
  severity: string;
  startDate?: Date;
  targetDate?: Date;
};

const toDate = (value: string | null | undefined) =>
  value ? new Date(value) : undefined;

const toFormValues = (action?: CustomEventAction): EventActionFormValues => ({
  name: action?.name ?? '',
  description: action?.description ?? '',
  poc: action?.poc ?? UNASSIGNED,
  severity: action?.severity ?? NO_SEVERITY,
  startDate: toDate(action?.startDate),
  targetDate: toDate(action?.targetDate),
});

type EventActionFormProps = {
  action?: CustomEventAction;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (input: EventActionInput) => void;
};

// Mounted only while the dialog is open, so each open starts from the action
const EventActionForm: React.FC<EventActionFormProps> = ({
  action,
  isSaving,
  onCancel,
  onSubmit,
}) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster = [] } = useRosters(groupGuid ? [groupGuid] : []);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EventActionFormValues>({ defaultValues: toFormValues(action) });

  const assigneeItems = useMemo(() => {
    const users = new Map<string, string>();
    roster.forEach((member) =>
      users.set(member.user_guid, member.user_name || member.email_address)
    );
    if (action?.poc && !users.has(action.poc)) {
      users.set(action.poc, action.pocUserName ?? action.poc);
    }
    return [
      { value: UNASSIGNED, label: 'Unassigned' },
      ...[...users].map(([value, label]) => ({ value, label })),
    ];
  }, [roster, action]);

  const submit = (values: EventActionFormValues) =>
    onSubmit({
      name: values.name.trim(),
      description: values.description,
      poc: values.poc === UNASSIGNED ? null : values.poc,
      severity:
        values.severity === NO_SEVERITY
          ? null
          : (values.severity as CustomEventActionSeverity),
      startDate: values.startDate ?? null,
      targetDate: values.targetDate ?? null,
    });

  return (
    <>
      <form
        id="event-action-form"
        className="tw:grid tw:gap-4"
        onSubmit={(e) => void handleSubmit(submit)(e)}
      >
        <Field data-invalid={!!errors.name}>
          <FieldLabel htmlFor="event-action-name">Task</FieldLabel>
          <Input
            id="event-action-name"
            aria-invalid={!!errors.name}
            {...register('name', {
              validate: (value) =>
                value.trim().length > 0 || 'Task name is required',
              maxLength: { value: 255, message: 'Task name is too long' },
            })}
          />
          <FieldError errors={[errors.name]} />
        </Field>
        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <Field>
            <FieldLabel>Assignee</FieldLabel>
            <Controller
              control={control}
              name="poc"
              render={({ field }) => (
                <Select
                  items={assigneeItems}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                >
                  <SelectTrigger aria-label="Assignee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assigneeItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field>
            <FieldLabel>Severity</FieldLabel>
            <Controller
              control={control}
              name="severity"
              render={({ field }) => (
                <Select
                  items={SEVERITY_ITEMS}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                >
                  <SelectTrigger aria-label="Severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_ITEMS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor="event-action-description">
            Description
          </FieldLabel>
          <Textarea
            id="event-action-description"
            rows={3}
            {...register('description')}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="event-action-start">Start</FieldLabel>
          <Controller
            control={control}
            name="startDate"
            render={({ field }) => (
              <DateTimePicker
                id="event-action-start"
                className="tw:w-full"
                defaultDateTime={field.value}
                onValueChange={field.onChange}
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="event-action-target">Due</FieldLabel>
          <Controller
            control={control}
            name="targetDate"
            render={({ field }) => (
              <DateTimePicker
                id="event-action-target"
                className="tw:w-full"
                defaultDateTime={field.value}
                onValueChange={field.onChange}
              />
            )}
          />
        </Field>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="event-action-form" disabled={isSaving}>
          {action ? 'Save' : 'Add'}
        </Button>
      </DialogFooter>
    </>
  );
};

type EventActionDialogProps = {
  open: boolean;
  action?: CustomEventAction;
  isSaving: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: EventActionInput) => void;
};

const EventActionDialog: React.FC<EventActionDialogProps> = ({
  open,
  action,
  isSaving,
  onOpenChange,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="tw:sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{action ? 'Edit task' : 'Add task'}</DialogTitle>
      </DialogHeader>
      <EventActionForm
        action={action}
        isSaving={isSaving}
        onCancel={() => onOpenChange(false)}
        onSubmit={onSubmit}
      />
    </DialogContent>
  </Dialog>
);

export default EventActionDialog;
